import React, {
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";

import PageLayout from "../../layouts/PageLayout";

import {
    Row,
    Col,
    Form,
    Button,
    Container,
} from "react-bootstrap";

import { TranslatorContext } from "../../context/Translator";

import {
    Link,
    useNavigate,
    useLocation
} from "react-router-dom";

import axios from "axios";

export default function IssuerProductEdit() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Accept item either directly or nested under lister_products[0]
    const rawItem = location.state?.item;
    const passedItem = rawItem?.lister_products?.[0] ? rawItem.lister_products[0] : rawItem;

    // --------------------------------------------------
    // Loading / Submit
    // --------------------------------------------------

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // --------------------------------------------------
    // API Data
    // --------------------------------------------------

    const [categories, setCategories] = useState([]);
    const [childCategories, setChildCategories] = useState([]);
    const [listers, setListers] = useState([]);
    const [mplacers, setMplacers] = useState([]);
    const [brands, setBrands] = useState([]);
    const [stores, setStores] = useState([]);
    const [selectedParentCategory, setSelectedParentCategory] = useState("");

    // --------------------------------------------------
    // Form
    // --------------------------------------------------

    const [formData, setFormData] = useState({
        category_id: "",
        lister_id: "",
        mplacer_id: "",
        brand_id: "",
        store_id: "",
        product_condition: "Used",
        selling_style: "Fixed",
        title: "",
        short_buying_price: "",
        buying_price: "",
        sell_price_dollar: "",
        shipping_charges_dollar: "",
    });

    // --------------------------------------------------
    // Image & Previews
    // --------------------------------------------------

    const [firstImg, setFirstImg] = useState(null);
    const [imgPreview, setImgPreview] = useState(null);

    // --------------------------------------------------
    // Errors & Camera
    // --------------------------------------------------

    const [errors, setErrors] = useState({});
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const videoRef = useRef(null);

    // ==================================================
    // POPULATE CATEGORIES (Parent & Sub Category Selection)
    // ==================================================

    const handleCategoryMapping = useCallback((categoriesList, selectedCategoryId) => {
        if (!selectedCategoryId || !categoriesList.length) return;

        const targetCatId = parseInt(selectedCategoryId, 10);

        for (const parent of categoriesList) {
            // Check if selected ID is parent itself
            if (parent.id === targetCatId) {
                setSelectedParentCategory(String(parent.id));
                setChildCategories(parent.child_categories || []);
                return;
            }

            // Check if selected ID is in child_categories
            if (Array.isArray(parent.child_categories)) {
                const matchedChild = parent.child_categories.find(
                    (child) => child.id === targetCatId
                );

                if (matchedChild) {
                    setSelectedParentCategory(String(parent.id));
                    setChildCategories(parent.child_categories);
                    setFormData((prev) => ({
                        ...prev,
                        category_id: String(matchedChild.id),
                    }));
                    return;
                }
            }
        }
    }, []);

    // ==================================================
    // FETCH INITIAL DATA
    // ==================================================

    const fetchInitialData = useCallback(async () => {
        setLoading(true);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/issuer/products/initial-data`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data.success) {
                const data = response.data.data;
                const fetchedCategories = data.product_categories || [];

                setCategories(fetchedCategories);
                setListers(data.listers || []);
                setMplacers(data.mplacers || []);
                setBrands(data.brands || []);
                setStores(data.stores || []);

                // Pre-fill form values when item is available
                if (passedItem) {
                    const catId = passedItem.category?.id || "";

                    setFormData({
                        title: passedItem.title || "",
                        category_id: catId,
                        lister_id: passedItem.lister?.id || rawItem?.lister?.id || "",
                        mplacer_id: passedItem.marketPlacer?.id || rawItem?.marketPlacer?.id || "",
                        brand_id: passedItem.brand?.id || "",
                        store_id: passedItem.store?.id || rawItem?.store?.id || "",
                        product_condition: passedItem.product_condition || "used",
                        selling_style: passedItem.selling_style || "Fixed",
                        short_buying_price: passedItem.short_buying_price ?? "",
                        buying_price: passedItem.buying_price ?? "",
                        sell_price_dollar: passedItem.sell_price_dollar ?? "",
                        shipping_charges_dollar: passedItem.shipping_charges_dollar ?? "",
                    });

                    if (passedItem.first_img) {
                        setImgPreview(`${process.env.REACT_APP_IMG_URL}${passedItem.first_img}`);
                    }

                    if (catId) {
                        handleCategoryMapping(fetchedCategories, catId);
                    }
                }
            }
        } catch (error) {
            console.error("Error loading initial form data:", error);
        } finally {
            setLoading(false);
        }
    }, [passedItem, rawItem, handleCategoryMapping]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Cleanup camera stream
    useEffect(() => {
        return () => {
            if (cameraStream) {
                cameraStream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [cameraStream]);

    // ==================================================
    // PARENT CATEGORY CHANGE
    // ==================================================

    const handleParentCategoryChange = (e) => {
        const parentId = e.target.value;
        setSelectedParentCategory(parentId);

        const parentCat = categories.find(
            (cat) => cat.id === parseInt(parentId, 10)
        );

        if (parentCat && Array.isArray(parentCat.child_categories)) {
            setChildCategories(parentCat.child_categories);
        } else {
            setChildCategories([]);
        }

        setFormData((prev) => ({
            ...prev,
            category_id: "",
        }));

        if (errors.category_id) {
            setErrors((prev) => ({
                ...prev,
                category_id: null,
            }));
        }
    };

    // ==================================================
    // FORM CHANGE
    // ==================================================

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }
    };

    // ==================================================
    // IMAGE HANDLING
    // ==================================================

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        stopCamera();
        setFirstImg(file);
        setImgPreview(URL.createObjectURL(file));

        if (errors.first_img) {
            setErrors((prev) => ({
                ...prev,
                first_img: null,
            }));
        }
    };

    const openCamera = async () => {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert("Camera is not supported by this browser.");
                return;
            }

            stopCamera();

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: false,
            });

            setCameraStream(stream);
            setCameraOpen(true);

            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch((err) => console.error("Video play error:", err));
                }
            }, 100);
        } catch (error) {
            console.error("Camera error:", error);
            alert("Unable to open camera.");
        }
    };

    const stopCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop());
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraStream(null);
        setCameraOpen(false);
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) {
            alert("Camera is not ready yet.");
            return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext("2d");
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (!blob) return;

            const file = new File([blob], `product-${Date.now()}.jpg`, {
                type: "image/jpeg",
            });

            setFirstImg(file);
            setImgPreview(URL.createObjectURL(file));

            if (errors.first_img) {
                setErrors((prev) => ({
                    ...prev,
                    first_img: null,
                }));
            }
            stopCamera();
        }, "image/jpeg", 0.9);
    };

    const removeImage = () => {
        setFirstImg(null);
        setImgPreview(null);
    };

    // ==================================================
    // SUBMIT
    // ==================================================

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});

        const submitData = new FormData();

        Object.keys(formData).forEach((key) => {
            if (formData[key] !== null && formData[key] !== "") {
                submitData.append(key, formData[key]);
            }
        });

        if (firstImg) {
            submitData.append("first_img", firstImg);
        }
        submitData.append("_method", "PATCH");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/issuer/products/${passedItem?.id}`,
                submitData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data.success) {
                navigate("/issue-product-list");
            }
        } catch (error) {
            if (error.response && error.response.status === 422) {
                setErrors(error.response.data.errors || {});
            } else {
                console.error("Error storing product:", error);
                alert("Something went wrong while saving the product.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner"></div>
                    <p>Loading Product Setup...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Container fluid className="px-0">
                <Row>
                    <Col xl={12}>
                        <div className="mc-card">
                            <div className="mc-breadcrumb">
                                <h3 className="mc-breadcrumb-title">Edit Product</h3>

                                {passedItem?.rejection_reason && (
                                    <div className="alert alert-danger py-2 px-3 my-2 text-center" role="alert">
                                        <strong className="text-danger">{t("rejection reason")}: </strong>
                                        <span className="text-dark">{passedItem.rejection_reason}</span>
                                    </div>
                                )}

                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("home") || "Home"}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="/products" className="mc-breadcrumb-link">
                                            {t("products") || "Products"}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">Edit Product</li>
                                </ul>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col lg={8}>
                            <div className="mc-card mb-4">
                                <h4 className="card-title mb-4">Product Details</h4>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Store <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="store_id"
                                                value={formData.store_id}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.store_id}
                                            >
                                                <option value="">Select Store</option>
                                                {stores.map((store) => (
                                                    <option key={store.id} value={store.id}>
                                                        {store.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.store_id && errors.store_id[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Product Condition <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="product_condition"
                                                value={formData.product_condition}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.product_condition}
                                            >
                                                <option value="used">Used</option>
                                                <option value="new">New</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.product_condition && errors.product_condition[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Parent Category</Form.Label>
                                            <Form.Select
                                                value={selectedParentCategory}
                                                onChange={handleParentCategoryChange}
                                            >
                                                <option value="">Select ....</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Sub Category <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.category_id}
                                                disabled={!selectedParentCategory}
                                            >
                                                <option value="">Select ....</option>
                                                {childCategories.map((child) => (
                                                    <option key={child.id} value={child.id}>
                                                        {child.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.category_id && errors.category_id[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Title <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.title}
                                        placeholder="e.g. Rolex Submariner Date 126610LN"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.title && errors.title[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Brand <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="brand_id"
                                                value={formData.brand_id}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.brand_id}
                                            >
                                                <option value="">Select Brand</option>
                                                {brands.map((brand) => (
                                                    <option key={brand.id} value={brand.id}>
                                                        {brand.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.brand_id && errors.brand_id[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Selling Style <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="selling_style"
                                                value={formData.selling_style}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.selling_style}
                                            >
                                                <option value="Fixed">Fixed</option>
                                                <option value="Auction">Auction</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.selling_style && errors.selling_style[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Assign Lister</Form.Label>
                                            <Form.Select
                                                name="lister_id"
                                                value={formData.lister_id}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.lister_id}
                                            >
                                                <option value="">Select Lister (Optional)</option>
                                                {listers.map((lister) => (
                                                    <option key={lister.id} value={lister.id}>
                                                        {lister.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.lister_id && errors.lister_id[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Assign Marketplace User</Form.Label>
                                            <Form.Select
                                                name="mplacer_id"
                                                value={formData.mplacer_id}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.mplacer_id}
                                            >
                                                <option value="">Select Marketplace User (Optional)</option>
                                                {mplacers.map((mplacer) => (
                                                    <option key={mplacer.id} value={mplacer.id}>
                                                        {mplacer.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.mplacer_id && errors.mplacer_id[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <div className="mc-card mb-4">
                                <h4 className="card-title mb-4">Pricing Configuration</h4>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Buying Price <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="buying_price"
                                                value={formData.buying_price}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.buying_price}
                                                placeholder="0.00"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.buying_price && errors.buying_price[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Buying Price ($) <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="sell_price_dollar"
                                                value={formData.sell_price_dollar}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.sell_price_dollar}
                                                placeholder="0.00"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.sell_price_dollar && errors.sell_price_dollar[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Shipping Charges ($) <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="shipping_charges_dollar"
                                                value={formData.shipping_charges_dollar}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.shipping_charges_dollar}
                                                placeholder="0.00"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.shipping_charges_dollar && errors.shipping_charges_dollar[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Short Buying Price</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="short_buying_price"
                                                value={formData.short_buying_price}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.short_buying_price}
                                                placeholder="0.00"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.short_buying_price && errors.short_buying_price[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        </Col>

                        <Col lg={4}>
                            <div className="mc-card mb-4">
                                <h4 className="card-title mb-4">Main Product Image</h4>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Product Image <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept="image/jpg,image/jpeg,image/png,image/webp"
                                        onChange={handleImageChange}
                                        isInvalid={!!errors.first_img}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.first_img && errors.first_img[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <div className="text-center mb-3">
                                    <div className="text-muted mb-2">OR</div>
                                    <Button
                                        type="button"
                                        variant="dark"
                                        className="w-100"
                                        onClick={openCamera}
                                    >
                                        📷 Take Photo
                                    </Button>
                                </div>

                                {imgPreview && (
                                    <div className="text-center mt-3">
                                        <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                            <img
                                                src={imgPreview}
                                                alt="Product Preview"
                                                className="img-fluid rounded border"
                                                style={{ width: "100%", maxHeight: "300px", objectFit: "contain" }}
                                            />
                                            <Button
                                                type="button"
                                                variant="danger"
                                                size="sm"
                                                style={{
                                                    position: "absolute",
                                                    top: "8px",
                                                    right: "8px",
                                                    borderRadius: "50%",
                                                    width: "32px",
                                                    height: "32px",
                                                    padding: 0,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                                onClick={removeImage}
                                            >
                                                ×
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mc-card">
                                <Button
                                    type="submit"
                                    className="btn btn-primary w-100 mb-2"
                                    disabled={submitting}
                                >
                                    {submitting ? "Saving Product..." : "Save Product"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="w-100"
                                    onClick={() => navigate("/issue-product-list")}
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Container>

            {cameraOpen && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.92)",
                        zIndex: 99999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            maxWidth: "700px",
                            backgroundColor: "#000",
                            borderRadius: "12px",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                padding: "15px",
                                color: "#fff",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <h5 style={{ margin: 0 }}>Take Product Photo</h5>
                            <Button type="button" variant="danger" size="sm" onClick={stopCamera}>
                                Close
                            </Button>
                        </div>

                        <div
                            style={{
                                width: "100%",
                                background: "#000",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    width: "100%",
                                    maxHeight: "70vh",
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        </div>

                        <div style={{ padding: "20px", display: "flex", gap: "10px" }}>
                            <Button type="button" variant="primary" className="w-100" onClick={capturePhoto}>
                                📸 Capture Photo
                            </Button>
                            <Button type="button" variant="secondary" className="w-100" onClick={stopCamera}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
}